import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ImageUploader from "./ImageUploader";
import ImageStack from "./ImageStack";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import styles from "../../styles/Building MGMT/BLDGUnitDetails.module.css";

const API_BASE_URL = import.meta.env.VITE_API_URL;

const BLDGUnitDetails = ({ handleBack, unitId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // orderedImages holds objects { id, src } for drag-and-drop ordering
  const [orderedImages, setOrderedImages] = useState([]);
  const [unitDetails, setUnitDetails] = useState({
    Bedrooms: "",
    Bathrooms: "",
    SquareFeet: "",
    Parking: "",
    UtilitiesEstimate: "",
    Amenities: [],
  });
  // State for Lightbox viewer index. -1 means closed.
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Fetch existing unit details and images on mount.
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/properties/units/${unitId}/edit-details`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        // Assume data.ImageGallery is an array of URLs.
        const images = (data.ImageGallery || []).map((url, idx) => ({
          id: `existing-${idx}`,
          src: url,
        }));
        setOrderedImages(images);
        setUnitDetails({
          Bedrooms: data.Bedrooms,
          Bathrooms: data.Bathrooms,
          SquareFeet: data.SquareFeet,
          Parking: data.Parking,
          UtilitiesEstimate: data.UtilitiesEstimate,
          Amenities: data.Features || [],
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [unitId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUnitDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmenityToggle = (amenity) => {
    setUnitDetails((prev) => ({
      ...prev,
      Amenities: prev.Amenities.includes(amenity)
        ? prev.Amenities.filter((a) => a !== amenity)
        : [...prev.Amenities, amenity],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1. Update textual unit details.
      const updatePayload = {
        Bedrooms: parseFloat(unitDetails.Bedrooms),
        Bathrooms: parseFloat(unitDetails.Bathrooms),
        SquareFeet: parseInt(unitDetails.SquareFeet, 10),
        Parking: unitDetails.Parking,
        UtilitiesEstimate: parseFloat(unitDetails.UtilitiesEstimate),
        PetPolicy: unitDetails.Amenities.includes("Pet Friendly") ? "Allowed" : "Not Allowed",
        Features: unitDetails.Amenities,
      };

      const res = await fetch(`${API_BASE_URL}/properties/units/${unitId}/details`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updatePayload),
      });
      if (!res.ok) throw new Error("Failed to update unit details.");

      // 2. Save image order explicitly.
      const imageOrderRes = await fetch(`${API_BASE_URL}/properties/units/${unitId}/image-order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          orderedImageUrls: orderedImages.map((img) => img.src),
        }),
      });
      if (!imageOrderRes.ok) throw new Error("Failed to update image order.");

      handleBack();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;

  return (
    <div className={styles.container}>
      <button className={styles.backToUnitsButton} onClick={handleBack}>
        ← Back to Units
      </button>
      <h2>Unit Setup - Unit #{unitId}</h2>

      <section className={styles.section}>
        <label>Bedrooms:</label>
        <input
          type="number"
          name="Bedrooms"
          value={unitDetails.Bedrooms}
          onChange={handleInputChange}
        />

        <label>Bathrooms:</label>
        <input
          type="number"
          step="0.5"
          name="Bathrooms"
          value={unitDetails.Bathrooms}
          onChange={handleInputChange}
        />

        <label>Square Feet:</label>
        <input
          type="number"
          name="SquareFeet"
          value={unitDetails.SquareFeet}
          onChange={handleInputChange}
        />
      </section>

      <section className={styles.section}>
        <label>Parking:</label>
        <input
          type="text"
          name="Parking"
          value={unitDetails.Parking}
          onChange={handleInputChange}
        />

        <label>Estimated Utilities ($/month):</label>
        <input
          type="number"
          name="UtilitiesEstimate"
          value={unitDetails.UtilitiesEstimate}
          onChange={handleInputChange}
        />
      </section>

      <div className={styles.amenitiesContainer}>
        {[
          "In-Unit Washer & Dryer",
          "Stainless Steel Appliances",
          "Fitness Center",
          "Central Air Conditioning",
          "Pet Friendly",
          "Assigned Parking",
        ].map((amenity) => (
          <button
            key={amenity}
            className={`${styles.amenityButton} ${
              unitDetails.Amenities.includes(amenity) ? styles.selected : ""
            }`}
            onClick={() => handleAmenityToggle(amenity)}
          >
            {amenity}
          </button>
        ))}
      </div>

      <section className={styles.imagesSection}>
        {/* ImageStack for drag-and-drop reordering with an onImageClick callback to open Lightbox */}
        <ImageStack
          images={orderedImages}
          onOrderChange={setOrderedImages}
          onImageClick={(index) => setLightboxIndex(index)}
        />

        {/* ImageUploader immediately uploads new images and appends them to the ordered list */}
        <ImageUploader
          unitId={unitId}
          onUploadSuccess={(newImages) =>
            setOrderedImages((prev) => [...prev, ...newImages])
          }
        />
      </section>

      {/* Lightbox viewer for a fullscreen image experience */}
      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={orderedImages.map((img) => ({ src: img.src }))}
      />

      <button className={styles.submitButton} onClick={handleSubmit}>
        Save Unit Details
      </button>
    </div>
  );
};

export default BLDGUnitDetails;
